import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  Alert,
  Stack,
} from '@mui/material';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

const ALLOWED_DOMAINS = ['channel1.ai', 'billboard.com'];

function emailDomainAllowed(email: string): boolean {
  const at = email.lastIndexOf('@');
  if (at < 0) return false;
  const domain = email.slice(at + 1).toLowerCase();
  return ALLOWED_DOMAINS.includes(domain);
}

interface AuthGateProps {
  children: React.ReactNode;
}

export const AuthGate: React.FC<AuthGateProps> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setChecking(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (checking) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '70vh',
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!session) return <SignIn />;

  const email = session.user.email || '';
  if (!emailDomainAllowed(email)) {
    return <AccessDenied email={email} />;
  }

  return <>{children}</>;
};

/* -------------------------------------------------------------------------- */

const SignIn: React.FC = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const sendCode = async () => {
    setError(null);
    setInfo(null);
    const trimmed = email.trim();
    if (!trimmed) { setError('Enter your email.'); return; }
    if (!emailDomainAllowed(trimmed)) {
      setError('Use a @channel1.ai or @billboard.com email.');
      return;
    }
    setBusy(true);
    const { error: err } = await supabase.auth.signInWithOtp({
      email: trimmed,
      options: { emailRedirectTo: window.location.origin },
    });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    setStep('code');
  };

  const verifyCode = async () => {
    setError(null);
    const trimmed = code.replace(/\s+/g, '');
    if (!trimmed) { setError('Enter the code from the email.'); return; }
    setBusy(true);
    const { error: err } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: trimmed,
      type: 'email',
    });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    // On success, onAuthStateChange in AuthGate will pick up the new session.
  };

  const resendCode = async () => {
    setError(null);
    setBusy(true);
    const { error: err } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: window.location.origin },
    });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    setInfo('Sent another code.');
  };

  return (
    <SignInLayout>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
        Sign in to Billboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Access limited to @channel1.ai and @billboard.com emails.
      </Typography>

      {step === 'email' ? (
        <Box component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); sendCode(); }}>
          <TextField
            fullWidth
            autoFocus
            type="email"
            label="Email address"
            placeholder="you@channel1.ai"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            sx={{ mb: 2 }}
          />

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={busy}
            sx={{ py: 1.25 }}
            startIcon={busy ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
          >
            {busy ? 'Sending code…' : 'Send code'}
          </Button>
        </Box>
      ) : (
        <Box component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); verifyCode(); }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Code sent to
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
            {email}
          </Typography>

          <TextField
            fullWidth
            autoFocus
            label="Verification code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={busy}
            inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
            sx={{ mb: 2 }}
          />

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {info && <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert>}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={busy}
            sx={{ py: 1.25, mb: 2 }}
            startIcon={busy ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
          >
            {busy ? 'Verifying…' : 'Verify & enter'}
          </Button>

          <Stack direction="row" justifyContent="space-between">
            <Button
              variant="text"
              size="small"
              onClick={() => { setStep('email'); setCode(''); setError(null); setInfo(null); }}
              disabled={busy}
            >
              Use different email
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={resendCode}
              disabled={busy}
            >
              Resend code
            </Button>
          </Stack>
        </Box>
      )}
    </SignInLayout>
  );
};

/* -------------------------------------------------------------------------- */

const AccessDenied: React.FC<{ email: string }> = ({ email }) => (
  <SignInLayout>
    <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
      Access denied
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      <Box component="strong" sx={{ color: 'text.primary' }}>{email}</Box>
      {' '}isn't on the staff list. Access is limited to @channel1.ai and @billboard.com addresses.
    </Typography>
    <Button variant="outlined" onClick={() => supabase.auth.signOut()}>
      Sign out & try another
    </Button>
  </SignInLayout>
);

/* -------------------------------------------------------------------------- */

const SignInLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6, backgroundColor: '#f5f5f5' }}>
    <Container maxWidth="xs">
      <Paper elevation={1} sx={{ p: { xs: 3, sm: 4 } }}>
        {children}
      </Paper>
    </Container>
  </Box>
);
